import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { TrendIndicator } from "../TrendIndicator";
import { MenuItemsAnalyticsResponse, MenuItemsSortField, PAGE_SIZE_OPTIONS } from "../types";

const TREND_TOOLTIP =
  "Trend compares the selected period to the previous matching period (e.g. This month vs. Last month).";

interface Column {
  field: MenuItemsSortField;
  label: string;
  info?: boolean;
}

const COLUMNS: Column[] = [
  { field: "rank", label: "Rank" },
  { field: "name", label: "Menu Item" },
  { field: "mealType", label: "Meal Type" },
  { field: "averageRating", label: "Average Rating" },
  { field: "totalOrders", label: "Total Orders" },
  { field: "users", label: "Users" },
  { field: "avgOrderPerUser", label: "Avg Order/User" },
  { field: "trend", label: "Trend", info: true },
];

interface MenuItemsTableProps {
  data: MenuItemsAnalyticsResponse | null;
  loading: boolean;
  error: boolean;
  sortBy: MenuItemsSortField;
  sortDir: "asc" | "desc";
  page: number; 
  pageSize: number;
  onSort: (field: MenuItemsSortField) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export const MenuItemsTable = ({
  data,
  loading,
  error,
  sortBy,
  sortDir,
  page,
  pageSize,
  onSort,
  onPageChange,
  onPageSizeChange,
}: MenuItemsTableProps) => {
  const rows = data?.items ?? [];
  const totalCount = data?.meta.totalCount ?? 0;

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUMNS.map(col => (
                <TableCell key={col.field} align="left" sortDirection={sortBy === col.field ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === col.field}
                    direction={sortBy === col.field ? sortDir : "desc"}
                    onClick={() => onSort(col.field)}
                  >
                    {col.label}
                    {col.info && (
                      <Tooltip title={TREND_TOOLTIP}>
                        <InfoOutlinedIcon sx={{ fontSize: 14, ml: 0.5, color: "text.secondary" }} />
                      </Tooltip>
                    )}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 &&
              Array.from({ length: pageSize }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  {COLUMNS.map(col => (
                    <TableCell key={col.field} align="left">
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {!loading && error && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6, color: "text.secondary" }}>
                  Unable to load menu items. Please refresh.
                </TableCell>
              </TableRow>
            )}

            {!loading && !error && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6, color: "text.secondary" }}>
                  No menu items match the selected filters.
                </TableCell>
              </TableRow>
            )}

            {rows.map(row => (
              <TableRow key={row.id} hover>
                <TableCell>{row.rank}</TableCell>
                <TableCell sx={{ maxWidth: 280 }}>
                  <Tooltip title={row.name}>
                    <Typography variant="body2" noWrap>
                      {row.name}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>{row.mealType || "—"}</TableCell>
                <TableCell>{row.averageRating === null ? "—" : row.averageRating.toFixed(1)}</TableCell>
                <TableCell>{row.totalOrders.toLocaleString()}</TableCell>
                <TableCell>{row.users.toLocaleString()}</TableCell>
                <TableCell>{row.avgOrderPerUser.toFixed(2)}</TableCell>
                <TableCell>
                  <TrendIndicator trend={row.trendPercent} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        count={totalCount}
        rowsPerPage={pageSize}
        page={page - 1}
        onPageChange={(_, nextPage) => onPageChange(nextPage + 1)}
        onRowsPerPageChange={e => onPageSizeChange(Number(e.target.value))}
        labelRowsPerPage="Results per page"
      />
    </>
  );
};
